import { useData } from '@/contexts/data-context/use-data';
import { ErrorSection } from '@/design-system/error-section/error-section';
import { LoadingSection } from '@/design-system/loading/loading';
import { TermsAndConditionsView } from '@/types/entity-types';
import { postSwrFetcher } from '@/utils/post-swr-fetcher';
import * as Sentry from '@sentry/react';
import { AcceptKiidRequestType, InvestmentKiidAcceptanceResponseType } from 'PublicEntityTypes';
import { useState } from 'react';
import { toast } from 'sonner';
import useSWRImmutable from 'swr/immutable';
import useSWRMutation from 'swr/mutation';
import { TreasuryStep } from '../treasury-flow';
import { TREASURY_HELP_LINK } from '../treasury-page';
import { HoldingDetails } from '../types/treasury-types';
import { KiidAcceptanceModal } from './kiid-acceptance-modal';
import { TreasuryEmptyState } from './treasury-empty-state';

const ACCEPT_KIID_URL = '/api/treasury/kiid-acceptance';
const GB_TERMS_AND_CONDITIONS_URL = '/api/terms-and-conditions/GB/latest';

type AcceptanceStepVariant = Extract<TreasuryStep, 'EMPTY_STATE_NO_KIID'>;

type KiidAcceptanceStepProps = {
  stepConfig: {
    variant: AcceptanceStepVariant;
    holdingDetails: HoldingDetails;
    companyId: string;
    isin: string;
    onContinue: () => void;
  };
};

export const KiidAcceptanceStep = ({ stepConfig }: KiidAcceptanceStepProps) => {
  const { mutations } = useData();

  const [modalVariantOpen, setModalVariantOpen] = useState<AcceptanceStepVariant | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    data: termsAndConditions,
    isLoading: isLoadingTermsAndConditions,
    error: termsAndConditionsError,
  } = useSWRImmutable<TermsAndConditionsView>(
    // Product decision: GB Terms & Conditions apply to all users, regardless of legal entity country.
    GB_TERMS_AND_CONDITIONS_URL,
  );
  const { trigger: triggerKiidAcceptance } = useSWRMutation<
    InvestmentKiidAcceptanceResponseType,
    Error,
    string,
    AcceptKiidRequestType
  >(ACCEPT_KIID_URL, postSwrFetcher);

  if (isLoadingTermsAndConditions) {
    return <LoadingSection text="Loading..." />;
  }

  if (!termsAndConditions) {
    return <ErrorSection error={termsAndConditionsError} />;
  }

  const { variant, holdingDetails, onContinue } = stepConfig;

  const openHelpLink = () => {
    window.open(TREASURY_HELP_LINK, '_blank', 'noopener noreferrer');
  };

  const closeModal = () => setModalVariantOpen(null);

  return (
    <>
      <TreasuryEmptyState
        holdingDetails={holdingDetails}
        onLearnMore={openHelpLink}
        onContinue={() => {
          setModalVariantOpen(variant);
          onContinue();
        }}
      />

      {variant === 'EMPTY_STATE_NO_KIID' && (
        <KiidAcceptanceModal
          isOpen={modalVariantOpen === 'EMPTY_STATE_NO_KIID'}
          isLoading={isLoading}
          name={holdingDetails.name}
          netYield={holdingDetails.netYield}
          kiidLink={holdingDetails.kiidLink}
          prospectusLink={holdingDetails.prospectusLink}
          formattedUpdatedAtByFundManager={holdingDetails.formattedUpdatedAtByFundManager}
          onContinue={async () => {
            setIsLoading(true);

            try {
              await triggerKiidAcceptance({
                companyId: stepConfig.companyId,
                isin: stepConfig.isin,
                termsAndConditionsId: termsAndConditions.id,
              });

              await mutations.handleIncrementalUpdates();
            } catch (error) {
              Sentry.captureException(error);

              toast.error('Failed to open your account. Please try again or contact us if the issue persists.');
            } finally {
              setIsLoading(false);
            }
          }}
          onClose={closeModal}
        />
      )}
    </>
  );
};
